import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Download, Printer, Type, StepBack } from "lucide-react";
import { Button } from "../components/ui/button";
import myPdf from "../images/filingdetails.pdf"; // ✅ FIXED relative path
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import * as Constants from "../../constants";
import BackLoader from "../../components/backloader";

// Table text wraps (long case titles and parties) so a whole row fits without scrolling sideways.
const HEAD = "whitespace-normal align-bottom py-3 text-white";
// Inline so the header stays visible even where the precompiled landing CSS lacks a class.
const HEAD_STYLE = { color: "#ffffff", whiteSpace: "normal", verticalAlign: "bottom" };
const CELL = "whitespace-normal break-words align-top p-3";

export default function OnlineSearchResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [textSize, setTextSize] = useState("normal");
  const searchQuery = searchParams.get("q") || "";
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };
  const adjustTextSize = (size) => {
    setTextSize(size);
  };

  useEffect(() => {
    if (searchQuery != null) {
      onPublicSearch();
    }
  }, [searchQuery]);
  const onPublicSearch = async () => {
    //this to continue the process for cases that looks like it
    setLoading(true);
    const sear = {
      caseTitle: searchQuery,
      caseprocess: "all",
      filingtype: "all",
    };
    try {
      const resp = await fetch(`${Constants.apiUrl}/report/public-cases/0/20`, {
        method: "POST",
        body: JSON.stringify(sear),
        headers: {
          "Content-Type": "application/json, text/plain",
        },
      });

      const data = await resp.json();
      setLoading(false);
      console.log(data);
      if (data.code === 0) {
        setResults(data.items);
        setTotalCount(data.totalCount || (data.items || []).length);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      // setErr(Constants.serverErr);
      // onMove();
    }
  };
  const getFontSizeClass = () => {
    if (textSize === "large") return "text-lg";
    if (textSize === "small") return "text-sm";
    return "text-base";
  };

  const onDownload = async (dtype, caseid, casestatusid) => {
    const url = `${Constants.apiUrl}/report/general-case-details/${caseid}/${casestatusid}`;

    // Map doctype to MIME and extension

    const typeMap = {
      excel: {
        mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ext: "xlsx",
      },
      msword: {
        mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ext: "docx",
      },
      pdf: {
        mime: "application/pdf",
        ext: "pdf",
      },
    };

    try {
      setLoading(true);
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) throw new Error("Failed to download");

      const blob = await resp.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `report.${typeMap[dtype].ext}`; // Fixed filename
      link.click();
      window.URL.revokeObjectURL(downloadUrl); // Clean up
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Download failed:", error);
    }
  };

  const onClickDownload = (caseid, casestatusid) => {
    onDownload("pdf", caseid, casestatusid);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <CompactHeader />
      {/* Accessibility Controls */}
      <div className="bg-[#1E293B] text-white py-3 px-4 sticky top-[100px] z-40">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          <Type className="w-5 h-5" aria-hidden="true" />
          <div className="flex gap-2 ml-3">
            {["small", "normal", "large"].map((size) => (
              <button
                key={size}
                onClick={() => adjustTextSize(size)}
                className={`px-3 py-2 rounded-lg ${
                  textSize === size
                    ? "bg-white text-[#1E293B]"
                    : "bg-white/10 hover:bg-white/20"
                }`}
              >
                {size === "small" ? "A-" : size === "normal" ? "A" : "A+"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />

        {results != null && (
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-6">
              <h1
                className={`${getFontSizeClass()} text-gray-900 drop-shadow-sm mb-3`}
              >
                Online Search Results
              </h1>
              <p
                className={`${getFontSizeClass()} text-gray-800 drop-shadow-sm`}
              >
                Showing results for:{" "}
                <span className="font-semibold">"{searchQuery}"</span>
              </p>
              <p className={`${getFontSizeClass()} text-gray-700 mt-2`}>
                {totalCount} case(s) found{totalCount > (results?.length || 0) ? ` (showing the latest ${results?.length})` : ""}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 flex-wrap">
              <Button
                onClick={handleBack}
                variant="outline"
                className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
              >
                <StepBack className="w-4 h-4 mr-2" /> Back to Search
              </Button>
              {results?.length == 1 && (
                <>
                  <Button
                    onClick={handlePrint}
                    variant="outline"
                    className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print Results
                  </Button>
                  <Button
                    onClick={() =>
                      onClickDownload(
                        results[0].caseId,
                        results[0].caseStatusId,
                      )
                    }
                    variant="outline"
                    className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Results Table */}
      <section className="py-12 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="bg-[#155dfc] hover:bg-[#155dfc]" style={{ backgroundColor: "#155dfc" }}>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      REFERENCE NO
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      SUIT NO
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      PARTIES TO THE CASE
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      CASE CATEGORY
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      CASE STATUS
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      JUDGE NAME
                    </TableHead>
                    <TableHead className={`${getFontSizeClass()} ${HEAD}`} style={HEAD_STYLE}>
                      DOWNLOAD
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results?.map((caseItem, index) => (
                    <TableRow
                      key={caseItem.id}
                      className={`${
                        index % 2 === 0
                          ? "bg-gray-50 dark:bg-slate-700"
                          : "bg-white dark:bg-slate-800"
                      } hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors`}
                    >
                      <TableCell className={`${getFontSizeClass()} ${CELL} font-medium text-[#155dfc]`}>
                        {caseItem.referenceNumber}
                      </TableCell>
                      <TableCell className={`${getFontSizeClass()} ${CELL}`}>{caseItem.caseNumber}</TableCell>
                      <TableCell className={`${getFontSizeClass()} ${CELL} min-w-[220px]`}>{caseItem.caseTitle}</TableCell>
                      <TableCell className={`${getFontSizeClass()} ${CELL}`}>{caseItem.caseCategoryTitle}</TableCell>
                      <TableCell className={`${getFontSizeClass()} ${CELL}`}>{caseItem.caseStatusTitle}</TableCell>
                      <TableCell className={CELL}>
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 whitespace-normal text-left">
                          {Constants.judgeTitle(caseItem.judgeName, "N/A")}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top p-3">
                        <Button
                          title="Download PDF"
                          onClick={() =>
                            onClickDownload(
                              caseItem.caseId,
                              caseItem.caseStatusId,
                            )
                          }
                          variant="outline"
                          className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
                        >
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {results?.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-12 border-2 border-gray-200 dark:border-slate-700">
                <h3
                  className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-4`}
                >
                  No Results Found
                </h3>
                <p
                  className={`${getFontSizeClass()} text-gray-600 dark:text-gray-400 mb-6`}
                >
                  No matches found for "{searchQuery}".
                </p>
                <Link to="/online-search">
                  <Button className="bg-[#155dfc] hover:bg-[#6B1515]">
                    Return to Search
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <BackLoader open={loading} />
    </div>
  );
}
